import { useEffect, useMemo, useState } from 'react'
import { SearchSelect, type ComboboxItem } from '@/components/ui'
import { useIngredientSearch } from '../api/queries'
import type { IngredientSearchResult } from '../api/types'

/**
 * Module-scoped picker for formulation ingredients (Sprint 08b).
 *
 * Mirrors the `ProductPicker` shape so it slots into
 * `EditableGrid.renderEditor` — same `editorMode` + `onCancel` contract
 * — but sources its rows from `/api/formulations/search/ingredients`,
 * which returns each product with its Odoo `inci_names` already
 * resolved. The picker surfaces the INCI list in each row's secondary
 * line so operators see what they're linking before commit.
 *
 * On select, the picker emits the full `IngredientSearchResult` (or
 * null on clear) — caller populates `trade_name`, `product_id`, AND
 * snapshot `inci_names` on the ingredient row in one commit. The
 * subsequent GET-detail enrichment from the backend will refresh INCI
 * on next load anyway; the snapshot is just the optimistic display.
 *
 * Module-scoped intentionally — only Formulations consumes the
 * ingredient-with-INCI search shape today. Promoting to a `@/components/ui`
 * primitive should wait for a second consumer per `ops/CLAUDE.md`.
 */

export interface IngredientPickerProps {
  value: IngredientSearchResult | null
  onChange: (next: IngredientSearchResult | null) => void
  placeholder?: string
  /** Auto-open on mount (used by EditableGrid.renderEditor). */
  editorMode?: boolean
  onCancel?: () => void
  width?: number
  className?: string
  disabled?: boolean
}

export function IngredientPicker({
  value,
  onChange,
  placeholder = 'Search ingredients…',
  editorMode = false,
  onCancel,
  width = 380,
  className,
  disabled,
}: IngredientPickerProps) {
  const [query, setQuery] = useState<string>('')
  const [debounced, setDebounced] = useState<string>('')

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 200)
    return () => clearTimeout(id)
  }, [query])

  const { data, isFetching } = useIngredientSearch(debounced)
  const results = useMemo(() => data?.results ?? [], [data])

  const items: ComboboxItem[] = useMemo(
    () =>
      results.map((r) => ({
        value: String(r.id),
        label: r.name || r.default_code || `Product ${r.id}`,
        meta:
          r.inci_names.length > 0
            ? r.inci_names.join(', ')
            : r.default_code || undefined,
        keywords: r.default_code ? [r.default_code] : undefined,
      })),
    [results],
  )

  const handlePicked = (idStr: string) => {
    const id = Number(idStr)
    const row = results.find((r) => r.id === id)
    if (!row) return
    onChange(row)
  }

  const emptyMessage = isFetching
    ? 'Searching…'
    : debounced.length < 2
      ? 'Type 2+ characters to search'
      : 'No ingredients found'

  return (
    <SearchSelect
      variant="cell"
      items={items}
      value={value ? String(value.id) : undefined}
      selectedLabel={value ? value.name : null}
      onQueryChange={setQuery}
      onSelect={handlePicked}
      onClear={() => onChange(null)}
      editorMode={editorMode}
      onCancel={onCancel}
      width={width}
      placeholder={placeholder}
      emptyMessage={emptyMessage}
      shouldFilter={false}
      disabled={disabled}
      className={className}
    />
  )
}
